import type {Repository} from '@github-ui/agent-sessions-interface/components/repo-picker/repo'
import type {CopilotChatRepo} from '@github-ui/copilot-chat-types'
import {reactFetchJSON} from '@github-ui/verified-fetch'

const REPO_RESULTS_LIMIT = 10
const REPOSITORIES_SEARCH_PATH = '/github-copilot/chat/repositories_search'

// Raw repository shape returned by the dotcom `repositories_search` endpoint.
// Only the fields the picker consumes are typed here.
interface RawRepository {
  databaseId: number
  nameWithOwner: string
  isPrivate?: boolean
  copilotChatRestriction?: CopilotChatRepo['copilotChatRestriction']
}

function transformRepository(raw: RawRepository): Repository {
  return {
    id: raw.databaseId,
    name: raw.nameWithOwner,
    private: raw.isPrivate ?? false,
  }
}

async function requestRepos(
  extraParams: {q?: string; owner?: string},
  signal: AbortSignal | undefined,
): Promise<Repository[]> {
  const params = new URLSearchParams()
  if (extraParams.q) params.set('q', extraParams.q)
  if (extraParams.owner) params.set('owner', extraParams.owner)
  params.set('limit', String(REPO_RESULTS_LIMIT))
  params.set('includeChatRestriction', 'true')

  const response = await reactFetchJSON(`${REPOSITORIES_SEARCH_PATH}?${params.toString()}`, {
    method: 'GET',
    signal,
  })

  if (!response.ok) {
    throw new Error(`Failed to load repositories (${response.status})`)
  }

  const data = (await response.json()) as {repositories?: RawRepository[]}
  return (data.repositories ?? []).filter(repo => !repo.copilotChatRestriction).map(transformRepository)
}

/**
 * Fetches repositories for the repo picker from the same-origin dotcom
 * `repositories_search` endpoint via the React JSON fetch helper. This
 * deliberately bypasses copilot-chat's chat service/manager. It issues its own
 * request so the picker carries no dependency on copilot-chat. An empty `query`
 * returns the server's owner-prioritized default list.
 *
 * A scoped picker sends `owner` so the endpoint narrows the default list to
 * that account and ranks the viewer's recent activity there first. Without
 * `owner`, the endpoint ranks across every account the viewer is active in,
 * which yields nothing for an account they are only SSOed into.
 *
 * A typed query is sent as `q` instead. Scoped pickers search both
 * `<owner>/<name>` and globally to keep public repositories discoverable;
 * the owner path would drop top-repository prioritization for typed searches.
 */
export async function fetchRepos(query: string, signal?: AbortSignal, owner?: string | null): Promise<Repository[]> {
  const trimmedQuery = query.trim()
  if (trimmedQuery) return requestRepos({q: trimmedQuery}, signal)

  return requestRepos(owner ? {owner} : {}, signal)
}
